import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
    Container, Typography, Card, CardContent, Button, Grid, Box,
    List, ListItem, ListItemText, AppBar, Toolbar, Select, MenuItem, IconButton
} from '@mui/material';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import ScienceIcon from '@mui/icons-material/Science';
import MemoryIcon from '@mui/icons-material/Memory';
import DescriptionIcon from '@mui/icons-material/Description';
import StoreIcon from '@mui/icons-material/Store';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import MoneyOffIcon from '@mui/icons-material/MoneyOff';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import PublicIcon from '@mui/icons-material/Public';
import RouterIcon from '@mui/icons-material/Router';
import ChipIcon from '@mui/icons-material/SettingsInputComponent';
import DeviceIcon from '@mui/icons-material/Devices';
import SpeedIcon from '@mui/icons-material/Speed';
import HubIcon from '@mui/icons-material/Hub';
import PsychologyIcon from '@mui/icons-material/Psychology';
import GrassIcon from '@mui/icons-material/Grass';
import LandscapeIcon from '@mui/icons-material/Landscape';
import OpacityIcon from '@mui/icons-material/Opacity';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import { Dialog, DialogContent, DialogTitle } from '@mui/material';
import axios from 'axios';
import { LanguageContext } from '../App';
import { LANGUAGES, getText } from '../utils/translations';
import logo from '../assets/logo.png';

function Dashboard() {
    const navigate = useNavigate();
    const [farmers, setFarmers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedFeature, setSelectedFeature] = useState(null);
    const { language, setLanguage } = useContext(LanguageContext);

    useEffect(() => {
        fetchFarmers();
    }, []);

    const fetchFarmers = async () => {
        try {
            const response = await axios.get('/api/farmers');
            // Ensure we always set an array
            setFarmers(Array.isArray(response.data) ? response.data : []);
        } catch (error) {
            console.error('Error fetching farmers:', error);
            // Set empty array on error to prevent map errors
            setFarmers([]);
        } finally {
            setLoading(false);
        }
    };

    const handleEditFarmer = (farmerId, e) => {
        e.stopPropagation();
        navigate(`/register-farmer?edit=${farmerId}`);
    };

    const handleDeleteFarmer = async (farmerId, e) => {
        e.stopPropagation();
        if (window.confirm('Are you sure you want to delete this farmer?')) {
            try {
                await axios.delete(`/api/farmers/${farmerId}`);
                fetchFarmers(); // Refresh list
            } catch (error) {
                console.error('Error deleting farmer:', error);
                alert('Failed to delete farmer');
            }
        }
    };

    const features = [
        { icon: <MemoryIcon sx={{ fontSize: 40 }} className="neon-icon" />, key: 'iot_integration', color: '#2196F3', available: true },
        { icon: <ScienceIcon sx={{ fontSize: 40 }} className="neon-icon" />, key: 'ml_recommendations', color: '#4CAF50', available: true },
        { icon: <PublicIcon sx={{ fontSize: 40 }} className="neon-icon" />, key: 'multi_dataset', color: '#E91E63', available: true },
        { icon: <DescriptionIcon sx={{ fontSize: 40 }} className="neon-icon" />, key: 'soil_health_cards', color: '#FF9800', available: true },
        { icon: <AccountBalanceIcon sx={{ fontSize: 40 }} className="neon-icon" />, key: 'govt_integration', color: '#00BCD4', available: true },
        { icon: <PsychologyIcon sx={{ fontSize: 40 }} className="neon-icon" />, key: 'crop_quality_test', color: '#9C27B0', available: true },
        { icon: <MoneyOffIcon sx={{ fontSize: 40 }} className="neon-icon" />, key: 'subsidy_calc', color: '#F44336', available: false },
        { icon: <StoreIcon sx={{ fontSize: 40 }} className="neon-icon" />, key: 'dealer_locator', color: '#9C27B0', available: false },
    ];

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
    };

    const itemVariants = {
        hidden: { y: 20, opacity: 0 },
        visible: { y: 0, opacity: 1 }
    };

    return (
        <Box sx={{ minHeight: '100vh', backgroundColor: '#000000' }}>
            <AppBar position="sticky" elevation={0} sx={{ top: 0, zIndex: 1100, backgroundColor: 'rgba(0, 0, 0, 0.2)', backdropFilter: 'blur(20px)', borderBottom: 'none' }}>
                <Toolbar>
                    <img src={logo} alt="SoilSense" style={{ height: '80px', marginRight: '16px' }} />
                    <Typography variant="h6" component="div" sx={{ flexGrow: 1, color: '#4CAF50', fontWeight: 600, textShadow: '0 0 10px rgba(76, 175, 80, 0.5)' }}>
                        SoilSense - Smart Fertilizer Recommendation System
                    </Typography>
                    <Select
                        value={language}
                        onChange={(e) => setLanguage(e.target.value)}
                        sx={{
                            color: '#4CAF50',
                            '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(76, 175, 80, 0.5)' },
                            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#4CAF50' }
                        }}
                    >
                        {LANGUAGES.map((lang) => (
                            <MenuItem key={lang.code} value={lang.code}>{lang.nativeName}</MenuItem>
                        ))}
                    </Select>
                </Toolbar>
            </AppBar>

            <Container maxWidth="lg" sx={{ mt: 4, pb: 4 }} component={motion.div} variants={containerVariants} initial="hidden" animate="visible">
                <Typography variant="h4" gutterBottom className="text-shimmer" sx={{ fontWeight: 'bold', mb: 4 }} component={motion.h4} variants={itemVariants}>
                    {getText('dashboard', language)}
                </Typography>

                <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <Card className="premium-card" sx={{ height: '100%' }} component={motion.div} variants={itemVariants}>
                            <CardContent>
                                <Typography variant="h5" gutterBottom sx={{ color: '#4CAF50' }}>
                                    {getText('quick_actions', language)}
                                </Typography>
                                <Box sx={{ mt: 2 }}>
                                    <Button
                                        variant="contained"
                                        fullWidth
                                        startIcon={<PersonAddIcon />}
                                        onClick={() => navigate('/register-farmer')}
                                        sx={{ mb: 2, backgroundColor: '#4CAF50', '&:hover': { backgroundColor: '#45a049' } }}
                                    >
                                        {getText('register_farmer', language)}
                                    </Button>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Card className="premium-card" sx={{ height: '100%' }} component={motion.div} variants={itemVariants}>
                            <CardContent>
                                <Typography variant="h5" gutterBottom sx={{ color: '#4CAF50' }}>
                                    {getText('recent_farmers', language)}
                                </Typography>
                                {loading ? (
                                    <Typography>Loading...</Typography>
                                ) : farmers.length === 0 ? (
                                    <Typography sx={{ color: '#e0e0e0', mt: 2 }}>
                                        No farmers registered yet.
                                    </Typography>
                                ) : (
                                    <List>
                                        {Array.isArray(farmers) && farmers.slice(0, 5).map((farmer) => (
                                            <ListItem
                                                key={farmer.id}
                                                button
                                                onClick={() => navigate(`/soil-data-entry/${farmer.id}`)}
                                                sx={{
                                                    backgroundColor: '#000000',
                                                    mb: 1,
                                                    borderRadius: 1,
                                                    border: '1px solid rgba(76, 175, 80, 0.2)',
                                                    '&:hover': { backgroundColor: 'rgba(76, 175, 80, 0.1)' },
                                                    pr: 10,
                                                    cursor: 'pointer'
                                                }}
                                                secondaryAction={
                                                    <Box>
                                                        <IconButton
                                                            edge="end"
                                                            onClick={(e) => handleEditFarmer(farmer.id, e)}
                                                            sx={{ color: '#4CAF50', mr: 1 }}
                                                        >
                                                            <EditIcon />
                                                        </IconButton>
                                                        <IconButton
                                                            edge="end"
                                                            onClick={(e) => handleDeleteFarmer(farmer.id, e)}
                                                            sx={{ color: '#f44336' }}
                                                        >
                                                            <DeleteIcon />
                                                        </IconButton>
                                                    </Box>
                                                }
                                            >
                                                <ListItemText
                                                    primary={farmer.name}
                                                    secondary={`${farmer.village}, ${farmer.district}`}
                                                    primaryTypographyProps={{ style: { color: '#4CAF50', fontWeight: 'bold' } }}
                                                    secondaryTypographyProps={{ style: { color: '#e0e0e0' } }}
                                                />
                                            </ListItem>
                                        ))}
                                    </List>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12}>
                        <Typography variant="h5" gutterBottom sx={{ color: '#4CAF50', mt: 2, mb: 2 }} component={motion.h5} variants={itemVariants}>
                            {getText('system_features', language)}
                        </Typography>
                    </Grid>

                    {features.map((feature, idx) => (
                        <Grid item xs={12} sm={6} md={4} key={idx} component={motion.div} variants={itemVariants} whileHover={feature.available ? { scale: 1.05 } : {}} transition={{ type: 'spring', stiffness: 300 }}>
                            <Card
                                className="premium-card"
                                sx={{
                                    height: '100%',
                                    position: 'relative',
                                    opacity: feature.available ? 1 : 0.6,
                                    cursor: feature.available ? 'pointer' : 'default',
                                    '&:hover': feature.available ? { borderColor: feature.color } : {}
                                }}
                                onClick={() => {
                                    if (!feature.available) return;
                                    if (feature.key === 'govt_integration') {
                                        navigate('/government-schemes');
                                    } else if (feature.key === 'crop_quality_test') {
                                        const famId = farmers.length > 0 ? farmers[0].id : 1;
                                        navigate(`/crop-quality-test/${famId}`);
                                    } else if (['iot_integration', 'ml_recommendations', 'multi_dataset'].includes(feature.key)) {
                                        setSelectedFeature(feature);
                                    }
                                }}
                            >
                                <CardContent sx={{ textAlign: 'center' }}>
                                    <Box sx={{ color: feature.color, mb: 2 }}>{feature.icon}</Box>
                                    <Typography variant="h6" sx={{ color: '#fff', mb: 1, fontWeight: 'bold' }}>
                                        {getText(feature.key, language)}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: '#e0e0e0' }}>
                                        {getText(`${feature.key}_desc`, language)}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            </Container>

            {/* Feature Detail Modal */}
            <Dialog
                open={Boolean(selectedFeature)}
                onClose={() => setSelectedFeature(null)}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        backgroundColor: '#0a1929',
                        border: selectedFeature ? `1px solid ${selectedFeature.color}` : 'none',
                        boxShadow: selectedFeature ? `0 0 30px ${selectedFeature.color}33` : 'none',
                        borderRadius: 3,
                        overflow: 'hidden'
                    }
                }}
            >
                {selectedFeature && (
                    <>
                        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Box sx={{ color: selectedFeature.color }}>{selectedFeature.icon}</Box>
                                <Typography variant="h5" sx={{ color: '#fff', fontWeight: 'bold' }}>
                                    {getText(selectedFeature.key, language)}
                                </Typography>
                            </Box>
                            <IconButton onClick={() => setSelectedFeature(null)} sx={{ color: '#fff' }}>
                                <CloseIcon />
                            </IconButton>
                        </DialogTitle>
                        <DialogContent sx={{ mt: 2, pb: 4 }}>
                            {selectedFeature.key === 'iot_integration' && (
                                <Box component={motion.div} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
                                    <Grid container spacing={3}>
                                        <Grid item xs={12} md={6}>
                                            <Typography variant="h6" sx={{ color: selectedFeature.color, mb: 2 }}>Physical Components</Typography>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                                {[
                                                    { name: 'ESP32-E Dev Module', desc: 'Core processing & WiFi stack', icon: <RouterIcon /> },
                                                    { name: 'Soil NPK Sensor (RS485)', desc: 'Real-time Nitrogen, Phosphorus, Potassium monitoring', icon: <ChipIcon /> },
                                                    { name: 'OLED 0.96" Display', desc: 'On-field visualization node', icon: <DeviceIcon /> },
                                                    { name: 'DHT22 / BMP280', desc: 'Precision Humidity, Temperature & Pressure', icon: <SpeedIcon /> },
                                                    { name: 'RS485-TTL Module', desc: 'Industrial sensor communication', icon: <HubIcon /> }
                                                ].map((comp, i) => (
                                                    <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 1.5, backgroundColor: 'rgba(255,255,255,0.05)', borderRadius: 2 }}>
                                                        <Box sx={{ color: selectedFeature.color, opacity: 0.8 }}>{comp.icon}</Box>
                                                        <Box>
                                                            <Typography sx={{ color: '#fff', fontWeight: 600 }}>{comp.name}</Typography>
                                                            <Typography variant="caption" sx={{ color: '#aaa' }}>{comp.desc}</Typography>
                                                        </Box>
                                                    </Box>
                                                ))}
                                            </Box>
                                        </Grid>
                                        <Grid item xs={12} md={6} sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                                            <Box
                                                component={motion.div}
                                                animate={{ scale: [1, 1.05, 1], rotate: [0, 1, -1, 0] }}
                                                transition={{ duration: 4, repeat: Infinity }}
                                                sx={{
                                                    position: 'relative',
                                                    width: 180,
                                                    height: 180,
                                                    borderRadius: '50%',
                                                    border: `4px solid ${selectedFeature.color}`,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    boxShadow: `0 0 20px ${selectedFeature.color}`
                                                }}
                                            >
                                                <MemoryIcon sx={{ fontSize: 80, color: selectedFeature.color }} />
                                            </Box>
                                            <Typography sx={{ color: '#aaa', textAlign: 'center', mt: 3, maxWidth: 300 }}>
                                                Our IoT nodes use industrial-grade <b>RS485</b> protocol to ensure zero data loss in hash agricultural environments.
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </Box>
                            )}

                            {selectedFeature.key === 'ml_recommendations' && (
                                <Box component={motion.div} initial={{ opacity: 0 }} animate={{ opacity: 1 }} sx={{ minHeight: 350, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                                    <Box sx={{ textAlign: 'center', mb: 6 }}>
                                        <Typography variant="h6" sx={{ color: '#4CAF50', mb: 1, fontWeight: 'bold' }}>Intelligence-Powered Nutrition</Typography>
                                        <Typography variant="body2" sx={{ color: '#aaa' }}>Transforming raw soil data into high-yield precision plans</Typography>
                                    </Box>

                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', position: 'relative' }}>
                                        {/* Left Side: Input Data */}
                                        <Grid container sx={{ width: '100%', maxWidth: 600 }} alignItems="center" justifyContent="center">
                                            <Grid item xs={3}>
                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, alignItems: 'flex-end' }}>
                                                    {[
                                                        { icon: <GrassIcon />, label: 'Nitrogen' },
                                                        { icon: <LandscapeIcon />, label: 'Phosphorus' },
                                                        { icon: <OpacityIcon />, label: 'K / Moisture' }
                                                    ].map((item, i) => (
                                                        <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <Typography variant="caption" sx={{ color: '#aaa', display: { xs: 'none', sm: 'block' } }}>{item.label}</Typography>
                                                            <Box sx={{ color: '#4CAF50', backgroundColor: 'rgba(76, 175, 80, 0.1)', p: 1, borderRadius: 2 }}>
                                                                {item.icon}
                                                            </Box>
                                                        </Box>
                                                    ))}
                                                </Box>
                                            </Grid>

                                            <Grid item xs={6} sx={{ position: 'relative', display: 'flex', justifyContent: 'center' }}>
                                                {/* Connecting Particles Animation */}
                                                <Box sx={{ position: 'absolute', width: '100%', height: '100%', zIndex: 0 }}>
                                                    {Array.from({ length: 6 }).map((_, i) => (
                                                        <Box
                                                            key={i}
                                                            component={motion.div}
                                                            animate={{
                                                                x: [-100, 100],
                                                                opacity: [0, 1, 0],
                                                                y: [i * 20 - 50, 0]
                                                            }}
                                                            transition={{
                                                                duration: 2,
                                                                repeat: Infinity,
                                                                delay: i * 0.4,
                                                                ease: "easeInOut"
                                                            }}
                                                            sx={{
                                                                position: 'absolute',
                                                                width: 4,
                                                                height: 4,
                                                                backgroundColor: '#4CAF50',
                                                                borderRadius: '50%',
                                                                left: '50%'
                                                            }}
                                                        />
                                                    ))}
                                                </Box>

                                                {/* Central Core */}
                                                <Box
                                                    component={motion.div}
                                                    animate={{
                                                        boxShadow: [
                                                            '0 0 20px rgba(76, 175, 80, 0.2)',
                                                            '0 0 40px rgba(76, 175, 80, 0.6)',
                                                            '0 0 20px rgba(76, 175, 80, 0.2)'
                                                        ],
                                                        scale: [1, 1.05, 1]
                                                    }}
                                                    transition={{ duration: 2, repeat: Infinity }}
                                                    sx={{
                                                        width: 100,
                                                        height: 100,
                                                        borderRadius: '50%',
                                                        backgroundColor: '#0a1929',
                                                        border: '2px solid #4CAF50',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        zIndex: 1,
                                                        flexDirection: 'column'
                                                    }}
                                                >
                                                    <PsychologyIcon sx={{ fontSize: 50, color: '#4CAF50' }} />
                                                    <Typography variant="caption" sx={{ color: '#4CAF50', fontWeight: 'bold' }}>AI CORE</Typography>
                                                </Box>
                                            </Grid>

                                            <Grid item xs={3}>
                                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                                                    <Box
                                                        component={motion.div}
                                                        animate={{ x: [0, 5, 0] }}
                                                        transition={{ duration: 2, repeat: Infinity }}
                                                        sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2, backgroundColor: 'rgba(76, 175, 80, 0.1)', borderRadius: 2, border: '1px solid rgba(76, 175, 80, 0.3)' }}
                                                    >
                                                        <AssignmentTurnedInIcon sx={{ color: '#4CAF50' }} />
                                                        <Box>
                                                            <Typography variant="caption" sx={{ color: '#fff', fontWeight: 'bold', display: 'block' }}>Precision Plan</Typography>
                                                            <Typography variant="caption" sx={{ color: '#4CAF50' }}>100% Accurate</Typography>
                                                        </Box>
                                                    </Box>
                                                </Box>
                                            </Grid>
                                        </Grid>
                                    </Box>

                                    <Typography variant="body2" sx={{ color: '#aaa', textAlign: 'center', mt: 8, maxWidth: 500 }}>
                                        Our <b>Autonomous Recommendation Engine</b> scans thousands of NPK-Moisture combinations to find the peak biological potential for your crop.
                                    </Typography>
                                </Box>
                            )}

                            {selectedFeature.key === 'multi_dataset' && (
                                <Box component={motion.div} initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}>
                                    <Grid container spacing={4} alignItems="center">
                                        <Grid item xs={12} md={6}>
                                            <Box sx={{ position: 'relative', width: '100%', height: 300, backgroundColor: 'rgba(255,255,255,0.02)', borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                {/* India Map Mockup Animation */}
                                                <PublicIcon sx={{ fontSize: 180, color: 'rgba(233, 30, 99, 0.1)' }} />
                                                {Array.from({ length: 12 }).map((_, i) => (
                                                    <Box
                                                        key={i}
                                                        component={motion.div}
                                                        animate={{ opacity: [0, 1, 0], scale: [0, 1, 1.5] }}
                                                        transition={{ duration: 2, repeat: Infinity, delay: Math.random() * 2 }}
                                                        sx={{
                                                            position: 'absolute',
                                                            width: 8,
                                                            height: 8,
                                                            backgroundColor: '#E91E63',
                                                            borderRadius: '50%',
                                                            boxShadow: '0 0 10px #E91E63',
                                                            left: `${20 + Math.random() * 60}%`,
                                                            top: `${20 + Math.random() * 60}%`
                                                        }}
                                                    />
                                                ))}
                                            </Box>
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <Typography variant="h6" sx={{ color: '#E91E63', mb: 2 }}>Pan-India Soil Intelligence</Typography>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                                                {[
                                                    { label: 'Maharashtra Dataset', val: '10,700 Records', color: '#E91E63' },
                                                    { label: 'Crop Recommendation Set', val: '2,200 Balanced Records', color: '#E91E63' },
                                                    { label: 'Fertilizer Core Set', val: '8,000 Verified Samples', color: '#E91E63' }
                                                ].map((d, i) => (
                                                    <Box key={i}>
                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                                            <Typography variant="subtitle2" sx={{ color: '#fff' }}>{d.label}</Typography>
                                                            <Typography variant="subtitle2" sx={{ color: d.color }}>{d.val}</Typography>
                                                        </Box>
                                                        <Box sx={{ width: '100%', height: 8, backgroundColor: 'rgba(255,255,255,0.05)', borderRadius: 4, overflow: 'hidden' }}>
                                                            <Box
                                                                component={motion.div}
                                                                initial={{ width: 0 }}
                                                                animate={{ width: '100%' }}
                                                                transition={{ duration: 2, delay: i * 0.3 }}
                                                                sx={{ height: '100%', backgroundColor: d.color, boxShadow: `0 0 10px ${d.color}` }}
                                                            />
                                                        </Box>
                                                    </Box>
                                                ))}
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </Box>
                            )}
                        </DialogContent>
                    </>
                )}
            </Dialog>

            {/* Footer */}
            <Box
                sx={{
                    textAlign: 'center',
                    py: 3,
                    borderTop: '1px solid rgba(76, 175, 80, 0.2)',
                    backgroundColor: 'rgba(0, 0, 0, 0.3)',
                    backdropFilter: 'blur(10px)'
                }}
            >
                <Typography
                    variant="body2"
                    sx={{
                        color: '#81C784',
                        fontWeight: 500,
                        letterSpacing: '0.5px'
                    }}
                >
                    <Typography
                        variant="body2"
                        component="a"
                        href="https://github.com/Paldeepak079/SoilSense---Smart-Fertilizer-Recommendation-System"
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                            color: '#81C784',
                            fontWeight: 500,
                            letterSpacing: '0.5px',
                            textDecoration: 'none',
                            cursor: 'pointer',
                            '&:hover': {
                                color: '#A5D6A7',
                                textDecoration: 'underline'
                            }
                        }}
                    >
                        Crafted by Team_MADTech
                    </Typography>
                </Typography>
            </Box>
        </Box>
    );
}

export default Dashboard;
